import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Breadcrumb, Card, Statistic, Row, Col, Select, message, Modal, Tag, Empty } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
    TrophyOutlined,
    ReloadOutlined,
    EyeOutlined,
    CalendarOutlined,
    TeamOutlined,
    RightOutlined,
    EnvironmentOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CarOutlined,
    DollarOutlined
} from '@ant-design/icons';
import { rankingsService } from '../../common/services';
import dayjs from 'dayjs';
import Can from '../../components/can';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
};

const formatQuarter = (q) => {
    if (!q) return '';
    const match = q.match(/^(\d{4})-T(\d)$/);
    if (!match) return q;
    const quarterNames = { '1': 'Fév - Avr', '2': 'Mai - Jul', '3': 'Aoû - Oct', '4': 'Nov - Jan' };
    return `Trimestre ${match[2]} ${match[1]} (${quarterNames[match[2]]})`;
};

const formatQuarterShort = (q) => {
    if (!q) return '';
    const match = q.match(/^(\d{4})-T(\d)$/);
    if (!match) return q;
    return `T${match[2]} ${match[1]}`;
};

const QuarterlyRankings = () => {
    const [quarters, setQuarters] = useState([]);
    const [stats, setStats] = useState(null);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [recalculating, setRecalculating] = useState(false);
    const [selectedQuarter, setSelectedQuarter] = useState(null);
    const navigate = useNavigate();

    const loadCities = useCallback(async () => {
        try {
            const response = await rankingsService.getCities();
            if (response.success) {
                const loadedCities = response.data.cities || [];
                setCities(loadedCities);
                const defaultCity = loadedCities.find(c => c.isDefault);
                if (defaultCity && !selectedCity) {
                    setSelectedCity(defaultCity._id);
                }
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedCity) params.city = selectedCity;

            const response = await rankingsService.getQuarterlyRankings(params);
            if (response.success) {
                setQuarters(response.data.quarters || []);
                setStats(response.data);
            }

            const statsResponse = await rankingsService.getQuarterlyStats(params);
            if (statsResponse.success) {
                setStats(statsResponse.data);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des données');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [selectedCity]);

    useEffect(() => { loadCities(); }, [loadCities]);
    useEffect(() => { loadData(); }, [loadData]);

    const handleRecalculate = async () => {
        const targetQuarter = selectedQuarter || stats?.currentQuarter;
        Modal.confirm({
            title: 'Calculer le classement trimestriel',
            content: (
                <div>
                    <p>Voulez-vous calculer le classement trimestriel pour <strong>{formatQuarterShort(targetQuarter)}</strong> ?</p>
                    <p style={{ color: '#999', fontSize: 12 }}>
                        Cette opération va agréger les courses et revenus des classements mensuels,
                        puis récupérer les heures de connexion de chaque chauffeur via l'API Yandex.
                        Cela peut prendre plusieurs minutes selon le nombre de chauffeurs.
                    </p>
                    <div style={{ marginTop: 12, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                        <strong>Critères d'éligibilité (par mois, chaque mois doit être validé) :</strong>
                        <ul style={{ marginTop: 8, marginBottom: 0 }}>
                            <li>≥ 200 courses <strong>par mois</strong></li>
                            <li>≥ 500 000 FCFA de revenus <strong>par mois</strong></li>
                            <li>≥ 130 heures de connexion <strong>par mois</strong></li>
                        </ul>
                    </div>
                </div>
            ),
            okText: 'Calculer',
            cancelText: 'Annuler',
            onOk: async () => {
                setRecalculating(true);
                try {
                    const response = await rankingsService.recalculateQuarterly(targetQuarter);
                    if (response.success) {
                        message.success(`Classement trimestriel calculé avec succès ! ${response.data?.eligible || 0} chauffeurs éligibles.`);
                        loadData();
                    } else {
                        message.error(response.message || 'Erreur lors du calcul');
                    }
                } catch (error) {
                    message.error('Erreur lors du calcul');
                    console.error(error);
                } finally {
                    setRecalculating(false);
                }
            }
        });
    };

    const columns = [
        {
            title: 'Trimestre',
            dataIndex: 'period',
            key: 'period',
            render: (period) => (
                <span style={{ fontWeight: 600 }}>
                    <CalendarOutlined style={{ marginRight: 8, color: '#722ED1' }} />
                    {formatQuarterShort(period)}
                </span>
            )
        },
        {
            title: 'Mois',
            dataIndex: 'months',
            key: 'months',
            render: (months) => (
                <div>
                    {(months || []).map(m => {
                        const [, month] = m.split('-');
                        const names = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
                        return <Tag key={m} color="blue">{names[parseInt(month) - 1]}</Tag>;
                    })}
                </div>
            )
        },
        {
            title: 'Chauffeurs',
            dataIndex: 'totalDrivers',
            key: 'totalDrivers',
            render: (count) => <span><TeamOutlined style={{ marginRight: 8 }} />{count}</span>
        },
        {
            title: 'Éligibles',
            dataIndex: 'eligibleDrivers',
            key: 'eligibleDrivers',
            render: (count) => (
                <Tag color={count > 0 ? 'green' : 'default'}>
                    <CheckCircleOutlined style={{ marginRight: 4 }} />
                    {count} éligible{count > 1 ? 's' : ''}
                </Tag>
            )
        },
        {
            title: 'Top 1',
            key: 'topDriver',
            render: (_, record) => (
                record.topDriver ? (
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.topDriver.firstName} {record.topDriver.lastName}</div>
                        <div style={{ fontSize: 12, color: '#999' }}>{record.topDriver.phone}</div>
                        <div style={{ marginTop: 4 }}>
                            <Tag color="gold">{formatCurrency(record.topDriver.revenue)}</Tag>
                            <Tag color="blue">{record.topDriver.orders} courses</Tag>
                            <Tag color="purple">{record.topDriver.hours}h</Tag>
                        </div>
                    </div>
                ) : <span style={{ color: '#ccc' }}>Aucun éligible</span>
            )
        },
        {
            title: 'Calculé le',
            dataIndex: 'calculatedAt',
            key: 'calculatedAt',
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        const url = selectedCity
                            ? `/dashboard/quarterly-rankings/${record.period}?city=${selectedCity}`
                            : `/dashboard/quarterly-rankings/${record.period}`;
                        navigate(url);
                    }}
                    style={{ background: '#722ED1', borderColor: '#722ED1' }}
                >
                    Voir
                </Button>
            )
        }
    ];

    return (
        <div className="quarterly-rankings-page">
            <Breadcrumb
                separator={<RightOutlined />}
                items={[
                    { title: <Link to="/dashboard">Tableau de bord</Link> },
                    { title: 'Classement Trimestriel' }
                ]}
                style={{ marginBottom: 24 }}
            />

            {/* Filtre par ville */}
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <EnvironmentOutlined style={{ fontSize: 20, color: '#722ED1' }} />
                    <span style={{ fontWeight: 600 }}>Filtrer par ville :</span>
                    <Select
                        placeholder="Toutes les villes"
                        style={{ width: 250 }}
                        allowClear
                        value={selectedCity}
                        onChange={(value) => setSelectedCity(value)}
                        loading={cities.length === 0}
                    >
                        {cities.map(city => (
                            <Select.Option key={city._id} value={city._id}>
                                {city.name} {city.isDefault && '(Par défaut)'}
                            </Select.Option>
                        ))}
                    </Select>
                    {selectedCity && (
                        <Tag color="purple">
                            {cities.find(c => c._id === selectedCity)?.name || 'Ville sélectionnée'}
                        </Tag>
                    )}
                </div>
            </Card>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Trimestre courant"
                            value={stats?.currentQuarter ? formatQuarterShort(stats.currentQuarter) : '-'}
                            prefix={<CalendarOutlined style={{ color: '#722ED1' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Chauffeurs éligibles"
                            value={stats?.currentStats?.eligibleDrivers || 0}
                            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Trimestres calculés"
                            value={stats?.quartersCount || 0}
                            prefix={<TrophyOutlined style={{ color: '#722ED1' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card style={{ background: 'linear-gradient(135deg, #f9f0ff 0%, #fff 100%)' }}>
                        <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Critères d'éligibilité</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Tag icon={<CarOutlined />} color="blue">≥ 200 courses/mois</Tag>
                            <Tag icon={<DollarOutlined />} color="gold">≥ 500K FCFA/mois</Tag>
                            <Tag icon={<ClockCircleOutlined />} color="purple">≥ 130h/mois</Tag>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Top 5 Current */}
            {stats?.top5 && stats.top5.length > 0 && (
                <Card
                    title={
                        <span>
                            <TrophyOutlined style={{ marginRight: 8, color: '#722ED1' }} />
                            Top 5 - Super Lots ({formatQuarterShort(stats.currentQuarter)})
                        </span>
                    }
                    style={{ marginBottom: 24, borderColor: '#722ED1' }}
                >
                    <Row gutter={16}>
                        {stats.top5.map((driver, index) => (
                            <Col xs={24} sm={12} md={8} lg={4.8} key={index} style={{ marginBottom: 12 }}>
                                <Card
                                    size="small"
                                    style={{
                                        background: index === 0 ? 'linear-gradient(135deg, #f9f0ff 0%, #fff 100%)' : undefined,
                                        borderColor: index < 3 ? '#722ED1' : undefined
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div
                                            style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#722ED1',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: 18
                                            }}
                                        >
                                            {driver.rank}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600 }}>{driver.firstName} {driver.lastName}</div>
                                            <div style={{ color: '#999', fontSize: 12 }}>{driver.phone}</div>
                                            <div style={{ marginTop: 4 }}>
                                                <Tag color="gold" style={{ fontSize: 11 }}>{formatCurrency(driver.revenue)}</Tag>
                                            </div>
                                            <div style={{ marginTop: 2 }}>
                                                <Tag color="blue" style={{ fontSize: 10 }}>{driver.orders} courses</Tag>
                                                <Tag color="purple" style={{ fontSize: 10 }}>{driver.hours}h</Tag>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}

            {/* Table des trimestres */}
            <Card
                title={
                    <span>
                        <TrophyOutlined style={{ marginRight: 8, color: '#722ED1' }} />
                        Historique des Classements Trimestriels
                    </span>
                }
                extra={
                    <Can perm="rankings:update">
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <Select
                                placeholder="Trimestre à calculer"
                                style={{ width: 200 }}
                                allowClear
                                value={selectedQuarter}
                                onChange={setSelectedQuarter}
                            >
                                {quarters.map(q => (
                                    <Select.Option key={q.period} value={q.period}>
                                        {formatQuarterShort(q.period)}
                                    </Select.Option>
                                ))}
                                {/* Toujours afficher le trimestre courant */}
                                {stats?.currentQuarter && !quarters.find(q => q.period === stats.currentQuarter) && (
                                    <Select.Option key={stats.currentQuarter} value={stats.currentQuarter}>
                                        {formatQuarterShort(stats.currentQuarter)} (courant)
                                    </Select.Option>
                                )}
                            </Select>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined spin={recalculating} />}
                                onClick={handleRecalculate}
                                loading={recalculating}
                                style={{ background: '#722ED1', borderColor: '#722ED1' }}
                            >
                                Calculer
                            </Button>
                        </div>
                    </Can>
                }
            >
                {quarters.length === 0 && !isLoading ? (
                    <Empty
                        description={
                            <span>
                                Aucun classement trimestriel calculé.
                                <br />
                                <span style={{ color: '#999' }}>Cliquez sur "Calculer" pour lancer le premier calcul.</span>
                            </span>
                        }
                    />
                ) : (
                    <Table
                        dataSource={quarters}
                        columns={columns}
                        rowKey="period"
                        loading={isLoading}
                        pagination={false}
                    />
                )}
            </Card>
        </div>
    );
};

export default QuarterlyRankings;
